import React, { useState, useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
  Button,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Tooltip,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useBreakpointValue,
  Skeleton,
  SkeletonText,
  Grid,
} from '@chakra-ui/react';
import {
  FiDollarSign,
  FiTrendingUp,
  FiTrendingDown,
  FiAlertTriangle,
  FiInfo,
  FiExternalLink,
  FiRefreshCw,
  FiDownload,
  FiEye,
  FiArrowLeft,
  FiUsers,
  FiPieChart,
  FiBarChart,
  FiActivity,
  FiShield,
  FiTarget,
  FiZap,
} from 'react-icons/fi';
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  ReferenceLine,
  Area,
  AreaChart,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { useTranslation } from 'react-i18next';
import { formatDOR, formatUSD, formatAddress, formatDate } from '@/utils/formatters';
import { usePriceData } from '@/hooks/usePriceData';
import PageHeader from '@/components/common/PageHeader';
import ConnectButton from '@/components/common/ConnectButton';

// 储备数据接口
interface ReserveData {
  totalReserve: number;
  totalSupply: number;
  circulatingSupply: number;
  lockedSupply: number;
  backingRatio: number;
  repurchaseUsed: number;
  yieldEarnings: number;
  lastUpdated: Date;
}

// 回购历史接口
interface RepurchaseHistory {
  id: string;
  timestamp: string;
  usdtAmount: number;
  dorAmount: number;
  priceAtTime: number;
  reason: string;
  txHash: string;
  blockNumber: number;
  reserveRemaining: number;
}

// 系统统计接口
interface SystemStats {
  activeUsers: number;
  totalRecharges: number;
  avgInviteDepth: number;
  tvl: number;
  growthRate: number;
  yieldRate: number;
  totalTransactions: number;
  avgTransactionSize: number;
}

// 储备概览组件
const ReserveOverview: React.FC<{ currentPrice: number }> = ({ currentPrice }) => {
  const { t } = useTranslation('reserve');
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedStat, setSelectedStat] = useState<string>('');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // 模拟储备数据 - 基于流通量计算
  const reserveData: ReserveData = {
    totalReserve: 500000000, // 5亿 USDT
    totalSupply: 1000000000, // 10亿 DOR (固定)
    circulatingSupply: 200000000, // 2亿流通 (通过充值解锁)
    lockedSupply: 800000000, // 8亿锁定 (待充值解锁)
    backingRatio: 250, // 250% (基于流通量: 5亿 / (2亿 * 1) = 250%)
    repurchaseUsed: 100000000, // 1亿用于回购
    yieldEarnings: 20000000, // 2000万收益
    lastUpdated: new Date(),
  };

  // 计算基于流通量的背书比率
  const backingRatio = reserveData.circulatingSupply > 0 
    ? (reserveData.totalReserve / (reserveData.circulatingSupply * 1)) * 100 
    : 0;
  
  const circulatingPercent = (reserveData.circulatingSupply / reserveData.totalSupply) * 100;
  const lockedPercent = (reserveData.lockedSupply / reserveData.totalSupply) * 100;

  // 饼图数据 - 流通 vs 锁定 vs 已销毁
  const pieChartData = [
    { name: t('charts.labels.circulatingDOR'), value: reserveData.circulatingSupply, color: '#38A169' },
    { name: t('charts.labels.lockedDOR'), value: reserveData.lockedSupply, color: '#DD6B20' },
    { name: t('charts.labels.destroyedDOR'), value: 0, color: '#A0AEC0' }, // 示例数据
  ];

  // 储备趋势数据
  const reserveTrendData = [
    { date: '2025-01-20', reserve: 480000000, backingRatio: 240, circulating: 200000000 },
    { date: '2025-01-21', reserve: 485000000, backingRatio: 242.5, circulating: 200000000 },
    { date: '2025-01-22', reserve: 490000000, backingRatio: 245, circulating: 200000000 },
    { date: '2025-01-23', reserve: 495000000, backingRatio: 247.5, circulating: 200000000 },
    { date: '2025-01-24', reserve: 500000000, backingRatio: 250, circulating: 200000000 },
  ];

  const getBackingRatioColor = (ratio: number) => {
    if (ratio >= 100) return 'green';
    if (ratio >= 50) return 'yellow';
    return 'red';
  };

  const getBackingRatioStatus = (ratio: number) => {
    if (ratio >= 100) return t('status.excessBacking');
    if (ratio >= 50) return t('status.normal');
    return t('status.risk');
  };

  const handleStatClick = (statName: string) => {
    setSelectedStat(statName);
    onOpen();
  };

  return (
    <Box>
      {/* 统计卡片组 */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mb={6}>
        <Card bg={cardBg} border="1px" borderColor={borderColor} cursor="pointer" onClick={() => handleStatClick('reserve')}>
          <CardBody>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">{t('stats.totalReserve')}</StatLabel>
              <StatNumber color="blue.500" fontSize="xl">
                {formatUSD(reserveData.totalReserve)}
              </StatNumber>
              <StatHelpText fontSize="xs">
                <StatArrow type="increase" />
                {t('descriptions.accumulatedFromRecharge')}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} border="1px" borderColor={borderColor} cursor="pointer" onClick={() => handleStatClick('backing')}>
          <CardBody>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">{t('stats.backingRatio')}</StatLabel>
              <StatNumber color={`${getBackingRatioColor(backingRatio)}.500`} fontSize="xl">
                {backingRatio.toFixed(1)}%
              </StatNumber>
              <StatHelpText fontSize="xs">
                <Badge colorScheme={getBackingRatioColor(backingRatio)} size="sm">
                  {getBackingRatioStatus(backingRatio)}
                </Badge>
              </StatHelpText>
            </Stat>
            <Progress 
              value={Math.min(backingRatio, 300)} 
              max={300}
              colorScheme={getBackingRatioColor(backingRatio)} 
              size="sm" 
              mt={2}
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              仅计充值解锁的流通 DOR
            </Text>
          </CardBody>
        </Card>

        <Card bg={cardBg} border="1px" borderColor={borderColor} cursor="pointer" onClick={() => handleStatClick('price')}>
          <CardBody>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">{t('stats.priceAnchor')}</StatLabel>
              <StatNumber color={currentPrice < 0.99 ? 'red.500' : 'green.500'} fontSize="xl">
                {formatUSD(currentPrice)}
              </StatNumber>
              <StatHelpText fontSize="xs">
                {currentPrice < 0.99 ? (
                  <Text color="red.500">偏离 -{((1 - currentPrice) * 100).toFixed(1)}%</Text>
                ) : (
                  <Text color="green.500">锚定稳定</Text>
                )}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} border="1px" borderColor={borderColor} cursor="pointer" onClick={() => handleStatClick('circulating')}>
          <CardBody>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">{t('stats.circulatingDOR')}</StatLabel>
              <StatNumber color="green.500" fontSize="xl">
                {formatDOR(reserveData.circulatingSupply)}
              </StatNumber>
              <StatHelpText fontSize="xs">
                {t('descriptions.circulatingPercent', { percent: circulatingPercent.toFixed(1) })}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} border="1px" borderColor={borderColor} cursor="pointer" onClick={() => handleStatClick('locked')}>
          <CardBody>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">{t('stats.lockedDOR')}</StatLabel>
              <StatNumber color="orange.500" fontSize="xl">
                {formatDOR(reserveData.lockedSupply)}
              </StatNumber>
              <StatHelpText fontSize="xs">
                {t('descriptions.lockedPercent', { percent: lockedPercent.toFixed(1) })}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} border="1px" borderColor={borderColor} cursor="pointer" onClick={() => handleStatClick('repurchase')}>
          <CardBody>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">{t('stats.repurchaseUsed')}</StatLabel>
              <StatNumber color="red.500" fontSize="xl">
                {formatUSD(reserveData.repurchaseUsed)}
              </StatNumber>
              <StatHelpText fontSize="xs">
                {t('descriptions.repurchasePercent', { percent: ((reserveData.repurchaseUsed / reserveData.totalReserve) * 100).toFixed(1) })}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* 储备组成图表 */}
      <Card bg={cardBg} border="1px" borderColor={borderColor} mb={6}>
        <CardHeader>
          <Heading size="md">{t('charts.supplyDistribution')}</Heading>
        </CardHeader>
        <CardBody>
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
            <Box h="300px">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value: number) => [formatDOR(value), 'DOR']}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontSize="lg" fontWeight="bold" mb={2}>{t('charts.reserveTrend')}</Text>
                <Box h="200px">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={reserveTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <RechartsTooltip />
                      <Legend />
                      <Area 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="reserve" 
                        stroke="#3182CE" 
                        fill="#3182CE" 
                        fillOpacity={0.3}
                        name={t('charts.labels.reserveUSDT')}
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="backingRatio" 
                        stroke="#E53E3E" 
                        name={t('charts.labels.backingRatio')}
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </VStack>
          </Grid>
        </CardBody>
      </Card>

      {/* 风险警报 */}
      {backingRatio < 50 && (
        <Alert status="error" borderRadius="md" mb={6}>
          <AlertIcon />
          <Box>
            <AlertTitle>{t('alerts.systemRisk.title')}</AlertTitle>
            <AlertDescription>
              {t('alerts.systemRisk.description', { ratio: backingRatio.toFixed(1) })}
              <Button 
                variant="link" 
                colorScheme="blue" 
                size="sm" 
                ml={2}
                onClick={() => {
                  // 切换到回购历史tab
                  const tabs = document.querySelector('[role="tablist"]');
                  if (tabs) {
                    const repurchaseTab = tabs.children[1] as HTMLElement;
                    repurchaseTab?.click();
                  }
                }}
              >
                {t('alerts.systemRisk.viewRepurchaseHistory')}
              </Button>
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* 价格偏离警报 */}
      {currentPrice < 0.99 && (
        <Alert status="warning" borderRadius="md" mb={6}>
          <AlertIcon />
          <Box>
            <AlertTitle>{t('alerts.priceDeviation.title')}</AlertTitle>
            <AlertDescription>
              {t('alerts.priceDeviation.description', { deviation: ((1 - currentPrice) * 100).toFixed(1) })}
              <Button 
                variant="link" 
                colorScheme="blue" 
                size="sm" 
                ml={2}
                onClick={() => navigate('/invite')}
              >
                {t('alerts.priceDeviation.inviteUsers')}
              </Button>
              {t('alerts.priceDeviation.descriptionEnd')}
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* 流通量提示 */}
      {circulatingPercent < 10 && (
        <Alert status="info" borderRadius="md" mb={6}>
          <AlertIcon />
          <Box>
            <AlertTitle>{t('alerts.lowCirculation.title')}</AlertTitle>
            <AlertDescription>
              {t('alerts.lowCirculation.description', { percent: circulatingPercent.toFixed(1) })}
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* 详情解释模态 */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('modal.statDetails')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedStat === 'reserve' && (
              <VStack spacing={4} align="stretch">
                <Text fontWeight="bold">{t('modal.reserve.title')}</Text>
                <Text>{t('modal.reserve.description')}</Text>
                <Text fontSize="sm" color="gray.500">
                  {t('modal.reserve.formula')}
                </Text>
              </VStack>
            )}
            {selectedStat === 'backing' && (
              <VStack spacing={4} align="stretch">
                <Text fontWeight="bold">{t('modal.backing.title')}</Text>
                <Text>{t('modal.backing.description')}</Text>
                <Text fontSize="sm" color="gray.500">
                  {t('modal.backing.formula')}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {t('modal.backing.healthLevels')}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {t('modal.backing.note')}
                </Text>
              </VStack>
            )}
            {selectedStat === 'price' && (
              <VStack spacing={4} align="stretch">
                <Text fontWeight="bold">{t('modal.price.title')}</Text>
                <Text>{t('modal.price.description')}</Text>
                <Text fontSize="sm" color="gray.500">
                  {t('modal.price.range')}
                </Text>
              </VStack>
            )}
            {selectedStat === 'circulating' && (
              <VStack spacing={4} align="stretch">
                <Text fontWeight="bold">{t('modal.circulating.title')}</Text>
                <Text>{t('modal.circulating.description')}</Text>
                <Text fontSize="sm" color="gray.500">
                  {t('modal.circulating.unlockMethod')}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {t('modal.circulating.currentCirculating', { 
                    amount: formatDOR(reserveData.circulatingSupply), 
                    percent: circulatingPercent.toFixed(1) 
                  })}
                </Text>
              </VStack>
            )}
            {selectedStat === 'locked' && (
              <VStack spacing={4} align="stretch">
                <Text fontWeight="bold">{t('modal.locked.title')}</Text>
                <Text>{t('modal.locked.description')}</Text>
                <Text fontSize="sm" color="gray.500">
                  {t('modal.locked.destroyMethod')}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {t('modal.locked.currentLocked', { 
                    amount: formatDOR(reserveData.lockedSupply), 
                    percent: lockedPercent.toFixed(1) 
                  })}
                </Text>
              </VStack>
            )}
            {selectedStat === 'repurchase' && (
              <VStack spacing={4} align="stretch">
                <Text fontWeight="bold">{t('modal.repurchase.title')}</Text>
                <Text>{t('modal.repurchase.description')}</Text>
                <Text fontSize="sm" color="gray.500">
                  {t('modal.repurchase.mechanism')}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  {t('modal.repurchase.currentUsed', { 
                    amount: formatUSD(reserveData.repurchaseUsed), 
                    percent: ((reserveData.repurchaseUsed / reserveData.totalReserve) * 100).toFixed(1) 
                  })}
                </Text>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// 回购历史组件
const RepurchaseHistory: React.FC = () => {
  const { t } = useTranslation('reserve');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const isMobile = useBreakpointValue({ base: true, md: false });

  // 模拟回购历史数据
  const repurchaseHistory: RepurchaseHistory[] = [
    {
      id: '1',
      timestamp: '2025-01-24 14:30:00',
      usdtAmount: 100000,
      dorAmount: 111111,
      priceAtTime: 0.90,
      reason: t('repurchaseHistory.autoRepurchase', { threshold: '0.95' }),
      txHash: '0x1234567890abcdef1234567890abcdef12345678',
      blockNumber: 12345678,
      reserveRemaining: 499900000,
    },
    {
      id: '2',
      timestamp: '2025-01-23 16:45:00',
      usdtAmount: 50000,
      dorAmount: 52632,
      priceAtTime: 0.95,
      reason: t('repurchaseHistory.autoRepurchase', { threshold: '0.98' }),
      txHash: '0xabcdef1234567890abcdef1234567890abcdef12',
      blockNumber: 12345670,
      reserveRemaining: 499950000,
    },
    {
      id: '3',
      timestamp: '2025-01-22 09:15:00',
      usdtAmount: 200000,
      dorAmount: 222222,
      priceAtTime: 0.90,
      reason: t('repurchaseHistory.autoRepurchase', { threshold: '0.95' }),
      txHash: '0x567890abcdef1234567890abcdef1234567890ab',
      blockNumber: 12345665,
      reserveRemaining: 499750000,
    },
  ];

  // 统计摘要
  const stats = useMemo(() => {
    const totalRepurchase = repurchaseHistory.reduce((sum, item) => sum + item.usdtAmount, 0);
    const totalDestroyed = repurchaseHistory.reduce((sum, item) => sum + item.dorAmount, 0);
    const avgPrice = totalRepurchase / totalDestroyed;
    
    return {
      totalRepurchase,
      totalDestroyed,
      avgPrice,
      count: repurchaseHistory.length,
    };
  }, [repurchaseHistory]);

  // 图表数据
  const chartData = repurchaseHistory.map(item => ({
    date: item.timestamp.split(' ')[0],
    usdtAmount: item.usdtAmount,
    dorAmount: item.dorAmount,
    price: item.priceAtTime,
  }));

  return (
    <Box>
      {/* 统计摘要 */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">{t('stats.totalRepurchase')}</StatLabel>
              <StatNumber color="red.500" fontSize="xl">
                {formatUSD(stats.totalRepurchase)}
              </StatNumber>
              <StatHelpText fontSize="xs">
                USDT
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">{t('stats.totalDestroyed')}</StatLabel>
              <StatNumber color="orange.500" fontSize="xl">
                {formatDOR(stats.totalDestroyed)}
              </StatNumber>
              <StatHelpText fontSize="xs">
                DOR
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">{t('stats.avgRepurchasePrice')}</StatLabel>
              <StatNumber color="blue.500" fontSize="xl">
                {formatUSD(stats.avgPrice)}
              </StatNumber>
              <StatHelpText fontSize="xs">
                USDT/DOR
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* 回购趋势图表 */}
      <Card bg={cardBg} border="1px" borderColor={borderColor} mb={6}>
        <CardHeader>
          <Heading size="md">{t('charts.repurchaseTrend')}</Heading>
        </CardHeader>
        <CardBody>
          <Box h="300px">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <RechartsTooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="usdtAmount" fill="#E53E3E" name={t('charts.labels.repurchaseUSDT')} />
                <Bar yAxisId="left" dataKey="dorAmount" fill="#ED8936" name={t('charts.labels.destroyDOR')} />
                <Line yAxisId="right" type="monotone" dataKey="price" stroke="#3182CE" name={t('charts.labels.price')} />
                <ReferenceLine yAxisId="right" y={1} stroke="#38A169" strokeDasharray="3 3" label={t('charts.labels.anchorPrice')} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardBody>
      </Card>

      {/* 回购历史列表 */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardHeader>
          <Heading size="md">{t('repurchaseHistory.title')}</Heading>
        </CardHeader>
        <CardBody>
          {isMobile ? (
            <Accordion allowToggle>
              {repurchaseHistory.map((item) => (
                <AccordionItem key={item.id}>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <HStack justify="space-between" align="center">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold" fontSize="sm">
                            {formatUSD(item.usdtAmount)} USDT
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {formatDate(item.timestamp)}
                          </Text>
                        </VStack>
                        <VStack align="end" spacing={1}>
                          <Text fontSize="sm" color="orange.500">
                            -{formatDOR(item.dorAmount)}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            {formatUSD(item.priceAtTime)}
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={4}>
                    <VStack spacing={3} align="stretch">
                      <Text fontSize="sm">{item.reason}</Text>
                      <HStack justify="space-between">
                        <Text fontSize="xs" color="gray.500">{t('repurchaseHistory.transactionHash')}</Text>
                        <Text fontSize="xs" fontFamily="mono">
                          {formatAddress(item.txHash)}
                        </Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontSize="xs" color="gray.500">{t('repurchaseHistory.reserveRemaining')}</Text>
                        <Text fontSize="xs">{formatUSD(item.reserveRemaining)}</Text>
                      </HStack>
                      <Button
                        size="xs"
                        variant="outline"
                        leftIcon={<FiExternalLink />}
                        onClick={() => window.open(`https://testnet.bscscan.com/tx/${item.txHash}`, '_blank')}
                      >
                        {t('repurchaseHistory.viewTransaction')}
                      </Button>
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{t('repurchaseHistory.time')}</Th>
                  <Th>{t('repurchaseHistory.repurchaseUSDT')}</Th>
                  <Th>{t('repurchaseHistory.destroyDOR')}</Th>
                  <Th>{t('repurchaseHistory.priceAtTime')}</Th>
                  <Th>{t('repurchaseHistory.triggerReason')}</Th>
                  <Th>{t('repurchaseHistory.actions')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {repurchaseHistory.map((item) => (
                  <Tr key={item.id}>
                    <Td fontSize="sm">{formatDate(item.timestamp)}</Td>
                    <Td>
                      <Text fontWeight="bold" color="red.500">
                        {formatUSD(item.usdtAmount)}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontWeight="bold" color="orange.500">
                        -{formatDOR(item.dorAmount)}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm">{formatUSD(item.priceAtTime)}</Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm" maxW="150px" noOfLines={2}>
                        {item.reason}
                      </Text>
                    </Td>
                    <Td>
                      <IconButton
                        size="sm"
                        variant="ghost"
                        icon={<FiExternalLink />}
                        onClick={() => window.open(`https://testnet.bscscan.com/tx/${item.txHash}`, '_blank')}
                        aria-label={t('repurchaseHistory.viewTransaction')}
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}

          {repurchaseHistory.length === 0 && (
            <Box textAlign="center" py={8}>
              <Text color="gray.500">{t('repurchaseHistory.noRecords')}</Text>
            </Box>
          )}
        </CardBody>
      </Card>
    </Box>
  );
};

// 系统统计组件
const SystemStats: React.FC = () => {
  const { t } = useTranslation('reserve');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const navigate = useNavigate();

  // 模拟系统统计数据 - 基于一级邀请
  const systemStats: SystemStats = {
    activeUsers: 10000,
    totalRecharges: 1000000000, // 10亿
    avgInviteDepth: 5, // 改为平均一级邀请数
    tvl: 800000000, // 8亿
    growthRate: 10,
    yieldRate: 4,
    totalTransactions: 50000,
    avgTransactionSize: 20000,
  };

  // 计算平均一级邀请数
  const avgDirectReferrals = systemStats.avgInviteDepth; // 平均一级邀请数
  const getReferralStatus = (count: number) => {
    if (count > 3) return { color: 'green', status: t('status.highActivity') };
    if (count >= 1) return { color: 'yellow', status: t('status.mediumActivity') };
    return { color: 'red', status: t('status.lowActivity') };
  };

  const referralStatus = getReferralStatus(avgDirectReferrals);

  // 增长趋势数据
  const growthData = [
    { month: '2024-10', users: 8000, recharges: 800000000, tvl: 600000000, avgReferrals: 4.5 },
    { month: '2024-11', users: 8500, recharges: 850000000, tvl: 650000000, avgReferrals: 4.8 },
    { month: '2024-12', users: 9000, recharges: 900000000, tvl: 700000000, avgReferrals: 5.0 },
    { month: '2025-01', users: 10000, recharges: 1000000000, tvl: 800000000, avgReferrals: 5.2 },
  ];

  // 一级邀请分布数据
  const referralDistributionData = [
    { name: t('charts.labels.noInvites'), value: 50, color: '#E53E3E' }, // 50% 用户无邀请
    { name: t('charts.labels.oneToThree'), value: 30, color: '#ECC94B' },  // 30% 用户邀请1-3人
    { name: t('charts.labels.moreThanThree'), value: 20, color: '#38A169' },   // 20% 用户邀请>3人
  ];

  return (
    <Box>
      {/* 统计卡片组 */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">{t('stats.avgInvites')}</StatLabel>
              <StatNumber color={`${referralStatus.color}.500`} fontSize="xl">
                {avgDirectReferrals.toFixed(1)} {t('descriptions.people')}
              </StatNumber>
              <StatHelpText fontSize="xs">
                <Badge colorScheme={referralStatus.color} size="sm">
                  {referralStatus.status}
                </Badge>
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">{t('stats.activeUsers')}</StatLabel>
              <StatNumber color="blue.500" fontSize="xl">
                {systemStats.activeUsers.toLocaleString()}
              </StatNumber>
              <StatHelpText fontSize="xs">
                <StatArrow type="increase" />
                {t('descriptions.participateRechargeInvite')}
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">{t('stats.totalRecharges')}</StatLabel>
              <StatNumber color="green.500" fontSize="xl">
                {formatUSD(systemStats.totalRecharges)}
              </StatNumber>
              <StatHelpText fontSize="xs">
                <StatArrow type="increase" />
                USDT
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel color="gray.500" fontSize="sm">{t('stats.lockedValue')}</StatLabel>
              <StatNumber color="orange.500" fontSize="xl">
                {formatUSD(systemStats.tvl)}
              </StatNumber>
              <StatHelpText fontSize="xs">
                <StatArrow type="increase" />
                TVL
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* 邀请分布图表 */}
      <Card bg={cardBg} border="1px" borderColor={borderColor} mb={6}>
        <CardHeader>
          <Heading size="md">{t('charts.inviteDistribution')}</Heading>
        </CardHeader>
        <CardBody>
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
            <Box h="300px">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={referralDistributionData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="value"
                  >
                    {referralDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value: number) => [`${value}%`, t('descriptions.userPercentage')]}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontSize="lg" fontWeight="bold" mb={2}>{t('charts.inviteTrend')}</Text>
                <Box h="200px">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={growthData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <RechartsTooltip />
                      <Bar dataKey="avgReferrals" fill="#38A169" name={t('charts.labels.avgInvites')} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </VStack>
          </Grid>
        </CardBody>
      </Card>

      {/* 增长趋势图表 */}
      <Card bg={cardBg} border="1px" borderColor={borderColor} mb={6}>
        <CardHeader>
          <Heading size="md">{t('charts.systemGrowth')}</Heading>
        </CardHeader>
        <CardBody>
          <Box h="400px">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={growthData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" />
                <YAxis yAxisId="right" orientation="right" />
                <RechartsTooltip />
                <Legend />
                <Line 
                  yAxisId="left" 
                  type="monotone" 
                  dataKey="users" 
                  stroke="#3182CE" 
                  name={t('charts.labels.activeUsers')}
                  strokeWidth={2}
                />
                <Line 
                  yAxisId="right" 
                  type="monotone" 
                  dataKey="recharges" 
                  stroke="#38A169" 
                  name={t('charts.labels.totalRecharges')}
                  strokeWidth={2}
                />
                <Line 
                  yAxisId="right" 
                  type="monotone" 
                  dataKey="tvl" 
                  stroke="#ED8936" 
                  name={t('charts.labels.lockedValue')}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardBody>
      </Card>

      {/* 可持续性指标 */}
      <Card bg={cardBg} border="1px" borderColor={borderColor} mb={6}>
        <CardHeader>
          <Heading size="md">{t('charts.sustainabilityMetrics')}</Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            <Box>
              <Text fontSize="lg" fontWeight="bold" color="green.500">
                +{systemStats.growthRate}%
              </Text>
              <Text fontSize="sm" color="gray.500">{t('systemStats.monthlyGrowth')}</Text>
            </Box>
            <Box>
              <Text fontSize="lg" fontWeight="bold" color="blue.500">
                {systemStats.yieldRate}%
              </Text>
              <Text fontSize="sm" color="gray.500">{t('systemStats.annualYield')}</Text>
            </Box>
            <Box>
              <Text fontSize="lg" fontWeight="bold">
                {systemStats.totalTransactions.toLocaleString()}
              </Text>
              <Text fontSize="sm" color="gray.500">{t('systemStats.totalTransactions')}</Text>
            </Box>
            <Box>
              <Text fontSize="lg" fontWeight="bold">
                {formatUSD(systemStats.avgTransactionSize)}
              </Text>
              <Text fontSize="sm" color="gray.500">{t('systemStats.avgTransactionSize')}</Text>
            </Box>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* 邀请活跃度警报 */}
      {avgDirectReferrals < 1 && (
        <Alert status="warning" borderRadius="md" mb={6}>
          <AlertIcon />
          <Box>
            <AlertTitle>{t('alerts.lowInviteActivity.title')}</AlertTitle>
            <AlertDescription>
              {t('alerts.lowInviteActivity.description', { count: avgDirectReferrals })}
              <Button 
                variant="link" 
                colorScheme="blue" 
                size="sm" 
                ml={2}
                onClick={() => navigate('/invite')}
              >
                {t('alerts.lowInviteActivity.joinInvite')}
              </Button>
              {t('alerts.lowInviteActivity.descriptionEnd')}
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* 邀请机制说明 */}
      <Card bg={cardBg} border="1px" borderColor={borderColor} mb={6}>
        <CardHeader>
          <Heading size="md">{t('systemStats.inviteMechanism')}</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="gray.600">
              <strong>邀请</strong>{t('systemStats.inviteDescription')}
            </Text>
            <Text fontSize="sm" color="gray.600">
              <strong>平均邀请数</strong>{t('systemStats.avgInviteDescription')}
            </Text>
            <Text fontSize="sm" color="gray.600">
              <strong>邀请越多</strong>，{t('systemStats.moreInvitesDescription')}
            </Text>
            <Button
              leftIcon={<FiUsers />}
              colorScheme="blue"
              onClick={() => navigate('/invite')}
            >
              {t('systemStats.startInvite')}
            </Button>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

// 主组件
const ReserveInfo: React.FC = () => {
  const { t } = useTranslation('reserve');
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const { priceData } = usePriceData();
  const currentPrice = priceData?.currentPrice || 1.0;
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // 未连接钱包时的提示
  if (!isConnected) {
    return (
      <Box>
        <VStack spacing={6} align="stretch">
          {/* 顶部区域 */}
          <PageHeader title={t('title')} />

          <Card>
            <CardBody>
              <VStack spacing={4}>
                <Text color="gray.600">
                  {t('connectWalletPrompt')}
                </Text>
                <ConnectButton />
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    );
  }

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        {/* 顶部区域 */}
        <PageHeader 
          title={t('title')} 
          subtitle={t('subtitle')}
        />

        {/* 主要内容 */}
        <Tabs variant="enclosed">
          <TabList>
            <Tab>{t('tabReserveOverview')}</Tab>
            <Tab>{t('tabRepurchaseHistory')}</Tab>
            <Tab>{t('tabSystemStats')}</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <ReserveOverview currentPrice={currentPrice} />
            </TabPanel>
            <TabPanel>
              <RepurchaseHistory />
            </TabPanel>
            <TabPanel>
              <SystemStats />
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* 底部行动区 */}
        <HStack justify="center" spacing={4}>
          <Button
            leftIcon={<FiExternalLink />}
            variant="outline"
            onClick={() => window.open('https://testnet.bscscan.com/address/0x1234567890123456789012345678901234567890', '_blank')}
          >
            {t('actions.viewChainAudit')}
          </Button>
          <Button
            leftIcon={<FiShield />}
            colorScheme="blue"
            onClick={() => window.open('https://snapshot.org', '_blank')}
          >
            {t('actions.participateGovernance')}
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default ReserveInfo; 